import React, { useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import Layout from "../components/Layout";
import { client } from "../lib/client.js";
import { galleryCampaignBySlugQuery } from "../lib/queries.js";

const CampaignPage = () => {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const galleryRef = useRef(null);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await client.fetch(galleryCampaignBySlugQuery(campaignId));
        setCampaign(data);
        window.scrollTo(0, 0);
      } catch (e) {
        setErr(e);
      }
    };
    fetchData();
  }, [campaignId]);

  useEffect(() => {
    if (!campaign) return;
    const gallery = galleryRef.current;
    if (!gallery) return;

    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;

    const handleMouseDown = (e) => {
      isDragging = true;
      gallery.classList.add("dragging");
      startX = e.pageX - gallery.offsetLeft;
      scrollLeft = gallery.scrollLeft;
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const x = e.pageX - gallery.offsetLeft;
      const walk = (x - startX) * 1.5;
      gallery.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUpOrLeave = () => {
      isDragging = false;
      gallery.classList.remove("dragging");
    };

    gallery.addEventListener("mousedown", handleMouseDown);
    gallery.addEventListener("mousemove", handleMouseMove);
    gallery.addEventListener("mouseup", handleMouseUpOrLeave);
    gallery.addEventListener("mouseleave", handleMouseUpOrLeave);

    return () => {
      gallery.removeEventListener("mousedown", handleMouseDown);
      gallery.removeEventListener("mousemove", handleMouseMove);
      gallery.removeEventListener("mouseup", handleMouseUpOrLeave);
      gallery.removeEventListener("mouseleave", handleMouseUpOrLeave);
    };
  }, [campaign]);

  if (err) {
    return (
      <Layout>
        <div className="mt-8 text-center">Campaign not found.</div>
      </Layout>
    );
  }

  if (!campaign) {
    return (
      <Layout>
        <div className="mt-8 text-center">Loading…</div>
      </Layout>
    );
  }

  // SEO bits
  const siteUrl = "https://this-belongs-to.com";
  const canonicalPath = `/pages/gallery/${campaignId}`;
  const canonicalUrl = `${siteUrl}${canonicalPath}`;

  const metaTitle = campaign.title
    ? `${campaign.title} | This Belongs To`
    : "Gallery Campaign | This Belongs To";

  let metaDescription = campaign.description || "";
  metaDescription = (metaDescription || "").replace(/\s+/g, " ").trim();
  if (!metaDescription) {
    metaDescription =
      "Campaign imagery from This Belongs To: underwear, clothing and stills from recent collections.";
  }
  if (metaDescription.length > 160) {
    metaDescription = `${metaDescription.slice(0, 157)}...`;
  }

  const ogImage = campaign.images?.[0] || null;

  return (
    <Layout>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        {ogImage && <meta property="og:image" content={ogImage} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
      </Helmet>

      <div className="overflow-hidden relative">
        <h1 className="text-lg font-light text-center mt-4 mb-6 italic uppercase">
          {campaign.title}
        </h1>

        {/* Horizontal Scrollable Gallery - DESKTOP ONLY */}
        <div
          ref={galleryRef}
          className="hidden md:flex md:w-full md:h-[85vh] overflow-x-auto snap-x snap-mandatory"
          style={{
            scrollBehavior: "smooth",
            gap: "0px",
          }}
        >
          {campaign.images.map((imgSrc, index) => (
            <div
              key={index}
              className="relative flex-shrink-0 snap-center"
              style={{
                flex: "0 0 auto",
                height: "100%",
              }}
            >
              <img
                src={imgSrc}
                alt={`Campaign image ${index + 1}`}
                className="w-auto h-full object-contain"
                style={{
                  margin: 0,
                  padding: 0,
                }}
              />
            </div>
          ))}
        </div>

        {/* Stacked Images - MOBILE ONLY */}
        <div className="flex flex-col space-y-4 md:hidden">
          {campaign.images.map((imgSrc, index) => (
            <img
              key={index}
              src={imgSrc}
              alt={`Campaign image ${index + 1}`}
              className="w-full h-auto object-contain"
              style={{ margin: 0, padding: 0 }}
            />
          ))}
        </div>

        {/* Video block (same style as Gallery page) */}
        {campaign.videoUrl && (
          <div className="w-11/12 md:w-3/5 mx-auto my-10">
            <div className="aspect-[16/9] w-full overflow-hidden">
              <video
                src={campaign.videoUrl}
                className="w-full h-full object-cover"
                controls
                muted
                autoPlay
                playsInline
              >
                Sorry, your browser doesn’t support embedded videos.
              </video>
            </div>
          </div>
        )}

        {/* Details / Credits */}
        <ul className="flex items-center flex-col justify-center mt-10">
          {campaign.photographer && (
            <li className="text-center mb-1">
              <span className="italic">Photographer</span>
              <br />
              {campaign.photographer}
            </li>
          )}
          {campaign.stylist && (
            <li className="text-center mb-1">
              <span className="italic">Stylist</span>
              <br />
              {campaign.stylist}
            </li>
          )}
          {campaign.creativeDirector && (
            <li className="text-center mb-1">
              <span className="italic">Creative Director</span>
              <br />
              {campaign.creativeDirector}
            </li>
          )}
          {campaign.creativeProducer && (
            <li className="text-center mb-1">
              <span className="italic">Creative Producer</span>
              <br />
              {campaign.creativeProducer}
            </li>
          )}
          {campaign.videographer && (
            <li className="text-center mb-1">
              <span className="italic">Videographer</span>
              <br />
              {campaign.videographer}
            </li>
          )}
          {campaign.gaffer && (
            <li className="text-center mb-1">
              <span className="italic">Gaffer</span>
              <br />
              {campaign.gaffer}
            </li>
          )}
          {campaign.lighting && (
            <li className="text-center mb-1">
              <span className="italic">Lighting</span>
              <br />
              {campaign.lighting}
            </li>
          )}
        </ul>
      </div>
    </Layout>
  );
};

export default CampaignPage;
